import React, { useState } from 'react';
import { FiltersValues, SavedFilterData } from '../types';
import { adjustDateRangeFilter, getJoinedLabel, getFilterEventDetails } from './utils';
import { createFilter, SaveFilterButton, SaveFilterModal } from '@ecosystem/newron-client-lib';
import { useSavedFiltersContext } from './savedFilters.provider';
import { SectionWithDefinitions } from './types';
import { useAnalyticsContext } from '../analytics/analytics.provider';

const DUPLICATE_NAME_API_ERROR = 'Name already exists';
const UNIQUE_NAME_ERROR_MESSAGE = 'Please provide a unique filter name to proceed';
const SAVE_ERROR_MESSGE = 'Error while saving the filter';

type Props = {
  values: FiltersValues;
  sectionsWithDefinitions: SectionWithDefinitions[];
};
function SaveFilterAction({ values, sectionsWithDefinitions }: Props) {
  const { filterContext, saveFilterEnabled, fetch } = useSavedFiltersContext();
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const { trackPageEvent } = useAnalyticsContext();

  const handleClose = () => {
    setErrorMessage('');
    setShowModal(false);
  };

  const handleSave = async (filterName?: string) => {
    try {
      const definitionLabels = sectionsWithDefinitions.flatMap(([_section, definitions]) =>
        definitions.map(def => ({ value: def.name, label: def.label }))
      );

      const filterData: SavedFilterData = {
        values: { ...values },
        definitionLabels: definitionLabels,
      };
      const body = {
        name: filterName,
        context: filterContext,
        data: { ...filterData },
      };

      await createFilter(body);

      trackPageEvent(...getFilterEventDetails('Save filter'));

      setErrorMessage('');
      fetch();
      handleClose();
    } catch (err) {
      if (err.response?.status === 422 && err.response?.data?.message === DUPLICATE_NAME_API_ERROR) {
        setErrorMessage(UNIQUE_NAME_ERROR_MESSAGE);
      } else {
        setErrorMessage(SAVE_ERROR_MESSGE);
      }
    }
  };

  const filterCriteriaGroup = sectionsWithDefinitions.map(([section, definitions]) => {
    return {
      name: section.label,
      filters: definitions.map(def => ({
        name: def.label,
        value: getJoinedLabel(values[def.name]),
      })),
    };
  });

  if (!saveFilterEnabled) {
    return null;
  }

  return (
    <>
      <SaveFilterButton
        onClick={() => {
          setShowModal(true);
          trackPageEvent(...getFilterEventDetails('Save filter button clicked'));
        }}
      />
      <SaveFilterModal
        isOpen={showModal}
        filterCriteria={filterCriteriaGroup}
        onSave={handleSave}
        onClose={handleClose}
        filterNameErrorMessage={errorMessage}
        onFilterNameChange={() => {}}
      />
    </>
  );
}

export { SaveFilterAction, adjustDateRangeFilter };
